import { Button, Dialog, DialogContent, Icons, SingleCombobox } from '@commenda/kakashi';
import { useSalesTaxProductTaxCodes } from '@commenda/api';
import {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import mixpanel from '@commenda/mixpanel';
import { useState } from 'react';

interface UpdateTaxCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (taxCode: string) => Promise<void>;
  totalSelected: number;
  corporationId: number;
}

const UpdateTaxCodeModal = ({
  isOpen,
  onClose,
  onUpdate,
  totalSelected,
  corporationId,
}: UpdateTaxCodeModalProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTaxCode, setSelectedTaxCode] = useState<string>('');

  const { data: taxCodes } = useSalesTaxProductTaxCodes({
    token: undefined,
    filters: { corporationId },
  });

  const handleClose = () => {
    onClose();
    setSelectedTaxCode('');
  };

  const handleUpdate = async () => {
    if (selectedTaxCode) {
      setIsUpdating(true);
      await onUpdate(selectedTaxCode);
      mixpanel.track(MIXPANEL_EVENTS.PRODUCT_MAPPED, {
        surface_area: MIXPANEL_SURFACE_AREA.INDIRECT_TAX_APP,
        type: 'bulk',
        total_products: totalSelected,
      });
      setIsUpdating(false);
      setSelectedTaxCode('');
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="min-w-[600px] p-0"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-center size-12 border border-primary rounded-lg">
            <Icons.InfoCircle className="size-5 text-fg-secondary" />
          </div>

          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-1">
              <h3 className="text-text-primary txt-large-bold">
                Update Tax Code
              </h3>
              <p className="txt-small-regular text-text-tertiary">
                You are about to update the tax code for {totalSelected} {totalSelected === 1 ? 'product' : 'products'}. Are you sure you want to proceed?
              </p>
            </div>

            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-1.5">
                <p className="txt-small-medium text-text-secondary">
                  Select Tax Code
                  <span className="txt-small-medium text-utility-violet-600 ml-0.5">*</span>
                </p>
                <SingleCombobox
                  options={taxCodes?.map((code) => ({
                    value: code.code,
                    label: code.code,
                  })) || []}
                  value={selectedTaxCode}
                  setValue={setSelectedTaxCode}
                  placeholder="Select a tax code"
                  _className="txt-medium-regular text-text-secondary h-11 px-3"
                  canClearSelection={false}
                />
              </div>

              <div className="flex gap-3 items-center justify-end">
                <Button variant="secondary" onClick={handleClose} size="sm">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleUpdate}
                  disabled={!selectedTaxCode || isUpdating}
                >
                  Update
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateTaxCodeModal;